/* Novaro Cart V1 — cross-sell add, automatic recommendations, drawer ↔ page sync, mobile sticky checkout, theme-editor preview.
   Shopify stays the single source of truth: every view is re-rendered from Shopify's section HTML, never from JS state. */
(() => {
  if (customElements.get('nv-cart-add')) return;

  const parse = (html) => new DOMParser().parseFromString(html, 'text/html');
  const pageEl = () => {
    const el = document.getElementById('main-cart-items');
    return el && el.dataset.nv ? el : null;
  };
  const recCache = new Map();

  // Replace the views we know about from a Shopify `sections` response.
  function renderSections(sections) {
    if (!sections) return;
    const page = pageEl();
    if (page && sections[page.dataset.id]) {
      const src = parse(sections[page.dataset.id]).querySelector('.js-contents');
      const tgt = page.querySelector('.js-contents');
      if (src && tgt) tgt.innerHTML = src.innerHTML;
    }
    if (sections['cart-icon-bubble']) {
      const b = document.getElementById('cart-icon-bubble');
      const src = parse(sections['cart-icon-bubble']).querySelector('.shopify-section');
      if (b && src) b.innerHTML = src.innerHTML;
    }
    if (sections['cart-drawer']) {
      const inner = document.querySelector('#CartDrawer .drawer__inner');
      const src = parse(sections['cart-drawer']).querySelector('.drawer__inner');
      if (inner && src) inner.innerHTML = src.innerHTML;
      const cd = document.querySelector('cart-drawer');
      if (cd) cd.classList.toggle('is-empty', !!(src && src.querySelector('.drawer__inner-empty')));
    }
  }

  function refreshPage() {
    const page = pageEl();
    if (!page) return Promise.resolve();
    return fetch(`${window.routes.cart_url}?section_id=${page.dataset.id}`)
      .then((r) => r.text())
      .then((html) => renderSections({ [page.dataset.id]: html }))
      .catch(() => {});
  }

  // Cart page: render the page section by its real id (keeps editor settings) and keep the drawer in sync.
  customElements.whenDefined('cart-items').then(() => {
    const CI = customElements.get('cart-items');
    if (!CI || CI.prototype.__nv) return;
    CI.prototype.__nv = true;
    const origGet = CI.prototype.getSectionsToRender;
    CI.prototype.getSectionsToRender = function () {
      const page = pageEl();
      if (!page) return origGet.call(this);
      const list = [
        { id: 'main-cart-items', section: page.dataset.id, selector: '.js-contents' },
        { id: 'cart-icon-bubble', section: 'cart-icon-bubble', selector: '.shopify-section' },
        { id: 'cart-live-region-text', section: 'cart-live-region-text', selector: '.shopify-section' },
      ];
      if (document.getElementById('CartDrawer')) list.push({ id: 'CartDrawer', section: 'cart-drawer', selector: '.drawer__inner' });
      return list;
    };
    const origUpdate = CI.prototype.onCartUpdate;
    CI.prototype.onCartUpdate = function () {
      if (this.tagName === 'CART-ITEMS' && pageEl()) return refreshPage();
      return origUpdate.call(this);
    };
  });

  // Drawer opened on the cart page: its edits also re-render the page.
  customElements.whenDefined('cart-drawer-items').then(() => {
    const CDI = customElements.get('cart-drawer-items');
    if (!CDI || CDI.prototype.__nv) return;
    CDI.prototype.__nv = true;
    const origGet = CDI.prototype.getSectionsToRender;
    CDI.prototype.getSectionsToRender = function () {
      const list = origGet.call(this);
      const page = pageEl();
      if (page) list.push({ id: 'main-cart-items', section: page.dataset.id, selector: '.js-contents' });
      return list;
    };
  });

  class NvCartAdd extends HTMLElement {
    connectedCallback() {
      if (this.bound) return;
      this.bound = true;
      this.button = this.querySelector('button');
      if (this.button) this.button.addEventListener('click', () => this.add());
    }

    setBusy(busy) {
      this.busy = busy;
      this.button.toggleAttribute('aria-busy', busy);
      this.button.classList.toggle('is-loading', busy);
      this.button.querySelector('.loading__spinner')?.classList.toggle('hidden', !busy);
    }

    showError(message) {
      const el = this.closest('.nv-xs__item')?.querySelector('.nv-xs__error');
      if (el) el.textContent = message || '';
    }

    add() {
      if (this.busy) return;
      const onPage = !!this.closest('#main-cart-items');
      const page = pageEl();
      const drawer = document.querySelector('cart-drawer');
      this.setBusy(true);
      this.showError('');

      const body = { items: [{ id: Number(this.dataset.variantId), quantity: Number(this.dataset.qty) || 1 }] };
      if (onPage && page) {
        body.sections = [page.dataset.id, 'cart-icon-bubble'];
        if (document.getElementById('CartDrawer')) body.sections.push('cart-drawer');
      } else if (drawer && drawer.getSectionsToRender) {
        body.sections = drawer.getSectionsToRender().map((s) => s.id);
      }
      if (body.sections) body.sections_url = window.location.pathname;

      fetch(`${window.routes.cart_add_url}.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(body),
      })
        .then((r) => r.json().then((data) => ({ ok: r.ok, data })))
        .then(({ ok, data }) => {
          if (!ok || data.status) throw new Error(data.description || data.message || window.cartStrings.error);
          if (onPage && page) {
            if (data.sections) renderSections(data.sections);
            else window.location.reload();
            return;
          }
          if (drawer && data.sections) {
            drawer.renderContents(data);
          } else {
            window.location = window.routes.cart_url;
            return;
          }
          if (typeof publish === 'function' && window.PUB_SUB_EVENTS) {
            publish(PUB_SUB_EVENTS.cartUpdate, { source: 'nv-cart-add', productVariantId: this.dataset.variantId, cartData: data });
          }
        })
        .catch((e) => {
          if (!this.isConnected) return;
          this.setBusy(false);
          this.showError(e.message || window.cartStrings.error);
        });
    }
  }
  customElements.define('nv-cart-add', NvCartAdd);

  class NvCartRecs extends HTMLElement {
    connectedCallback() {
      const url = this.dataset.url;
      if (!url) return;
      const key = `${url}|${this.dataset.key || ''}`;
      if (recCache.has(key)) return this.fill(recCache.get(key));
      fetch(url)
        .then((r) => (r.ok ? r.text() : ''))
        .then((html) => {
          recCache.set(key, html);
          if (this.isConnected) this.fill(html);
        })
        .catch(() => {});
    }

    fill(html) {
      if (!html) return;
      const items = [...parse(html).querySelectorAll('.nv-xs__item')].slice(0, Number(this.dataset.limit) || 2);
      const list = this.querySelector('.nv-xs__list');
      if (!items.length || !list) return;
      list.replaceChildren(...items);
      this.hidden = false;
    }
  }
  customElements.define('nv-cart-recs', NvCartRecs);

  // Mobile: compact checkout bar while the summary's Checkout button is off-screen.
  class NvCartSticky extends HTMLElement {
    connectedCallback() {
      const target = document.getElementById('NvCartCheckout');
      if (!target || !('IntersectionObserver' in window)) return;
      const mq = window.matchMedia('(max-width: 749px)');
      this.io = new IntersectionObserver(([entry]) => {
        const show = !entry.isIntersecting && mq.matches;
        this.hidden = !show;
        document.body.classList.toggle('nv-cart-sticky-on', show);
      });
      this.io.observe(target);
    }

    disconnectedCallback() {
      if (this.io) this.io.disconnect();
      document.body.classList.remove('nv-cart-sticky-on');
    }
  }
  customElements.define('nv-cart-sticky', NvCartSticky);

  if (window.Shopify && Shopify.designMode) {
    document.addEventListener('shopify:section:select', (e) => {
      if (e.detail.sectionId === 'cart-drawer') document.querySelector('cart-drawer')?.open();
    });
    document.addEventListener('shopify:section:deselect', (e) => {
      if (e.detail.sectionId === 'cart-drawer') document.querySelector('cart-drawer')?.close();
    });
  }
})();
